<script setup>
  import { ref, computed } from "vue";
  import { storeToRefs } from "pinia";
  import { useSlideScaling } from "@/utils/composables/useSlideScaling";
  import { useFormattedText } from "@/utils/composables/useFormattedText";
  import { useReportsV2Store } from "@stores/survey/reportsV2/reportsV2";
  import SlideTextElement from "./slide-preview/SlideTextElement.vue";
  import SlideImageElement from "./slide-preview/SlideImageElement.vue";
  import SlideChartElement from "./slide-preview/SlideChartElement.vue";

  const { adaptReportThemeStyle } = useFormattedText();
  const { currentReport } = storeToRefs(useReportsV2Store());

  const emit = defineEmits(["select-field"]);

  const props = defineProps({
    structure: {
      type: Object,
      default: null,
    },
    bindings: {
      type: Object,
      default: () => ({}),
    },
    editableFields: {
      type: Array,
      default: () => [],
    },
    previewImageUrl: {
      type: String,
      default: null,
    },
    showOverlays: {
      type: Boolean,
      default: false,
    },
    showBorders: {
      type: Boolean,
      default: false,
    },
    interactive: {
      type: Boolean,
      default: false,
    },
    selectedFieldName: {
      type: String,
      default: null,
    },
    aspectRatio: {
      type: String,
      default: "16/9",
    },
    chartTemplates: {
      type: Array,
      default: () => [],
    },
  });

  // Interaction state
  const hoveredFieldName = ref(null);
  const containerHovered = ref(false);
  const imageError = ref(false);

  // Slide scaling (EMU conversion + ResizeObserver)
  const structureRef = computed(() => props.structure);
  const { containerRef, referenceWidth, referenceHeight, scaleFactor, emuToPixels } =
    useSlideScaling(structureRef);

  const elements = computed(() => props.structure?.elements || []);

  const editableElements = computed(() => {
    const editableNames = props.editableFields.map((f) => f.name);
    return elements.value.filter((el) => editableNames.includes(el.name));
  });

  // Resolve the effective text style for an element:
  // report theme style (via report_theme_style_key on the editable field) overrides element default.
  function getTextStyle(elementName) {
    const textStyles = currentReport.value?.report_theme_text_styles || {};
    const field = props.editableFields.find((f) => f.name === elementName);
    const styleKey = field?.report_theme_style_key;
    if (styleKey && textStyles[styleKey]) {
      return adaptReportThemeStyle(textStyles[styleKey]);
    }
    const element = elements.value.find((el) => el.name === elementName);
    return element?.text_style || {};
  }

  const containerStyle = computed(() => ({
    aspectRatio: props.aspectRatio,
    width: "100%",
  }));

  function onElementClick(event, name) {
    if (props.interactive) {
      event.stopPropagation();
      emit("select-field", name);
    }
  }

  function getElementStyle(element) {
    const { left, top, width, height } = emuToPixels(element.position, element.size);

    // Extend by 1px on each side to cover sub-pixel gaps from CSS transform scaling
    const style = {
      position: "absolute",
      left: `${left - 1}px`,
      top: `${top - 1}px`,
      width: `${width + 2}px`,
      height: `${height + 2}px`,
    };

    // Apply extracted fill color if available (but not for charts with a template selected)
    const isChartWithTemplate = element.type === "chart" && getChartTemplate(element);
    if (element.fill_color && !isChartWithTemplate) {
      style.backgroundColor = element.fill_color;
    }

    // Apply extracted border if available
    if (element.border_color) {
      const borderWidth = element.border_width_pt ? `${element.border_width_pt}pt` : "1pt";
      style.border = `${borderWidth} solid ${element.border_color}`;
    }

    return style;
  }

  function getElementClass(element) {
    const baseClasses = element.type === "chart" ? "overflow-hidden" : "";

    // Interactive mode: hover/click styling
    if (props.interactive) {
      if (props.selectedFieldName === element.name) {
        return `${baseClasses} ring-2 ring-primary cursor-pointer`;
      }
      if (hoveredFieldName.value === element.name) {
        return `${baseClasses} ring-1 ring-primary cursor-pointer`;
      }
      if (containerHovered.value) {
        return `${baseClasses} outline-dashed outline-1 outline-primary/30 cursor-pointer`;
      }
      return `${baseClasses} cursor-pointer`;
    }

    // Legacy mode: showBorders behavior
    const borderClasses = props.showBorders ? "border-2 border-dashed border-status-warning" : "";

    if (element.type === "chart" && props.showBorders) {
      return `${baseClasses} ${borderClasses} rounded bg-status-info-soft/50`;
    }

    return `${baseClasses} ${borderClasses}`;
  }

  function getImageUrl(element) {
    return props.bindings[element.name]?.value || null;
  }

  function getChartTemplate(element) {
    const binding = props.bindings[element.name];
    if (!binding?.chart_template_id) return null;
    return props.chartTemplates.find((t) => t.id === binding.chart_template_id) || null;
  }

  function getElementDimensions(element) {
    const { width, height } = emuToPixels(element.position, element.size);
    return { width: Math.round(width), height: Math.round(height) };
  }

  function handleImageError() {
    imageError.value = true;
  }
</script>

<template>
  <div
    ref="containerRef"
    class="relative bg-white overflow-hidden border"
    :style="containerStyle"
    @mouseenter="interactive && (containerHovered = true)"
    @mouseleave="interactive && (containerHovered = false)">
    <!-- Background image (faithful preview) -->
    <img
      v-if="previewImageUrl"
      :key="previewImageUrl"
      :src="previewImageUrl"
      alt="Slide preview"
      class="absolute inset-0 w-full h-full object-contain"
      @error="handleImageError" />

    <!-- Fallback background when no image -->
    <div
      v-else
      class="absolute inset-0 bg-secondary flex items-center justify-center">
      <span class="text-foreground-subtle text-sm">Preview not available</span>
    </div>

    <!-- Dynamic overlay for editable fields (shown when showOverlays is true and we have a valid scale) -->
    <div
      v-if="showOverlays && scaleFactor > 0"
      class="absolute inset-0 overflow-hidden"
      @click="interactive && emit('select-field', null)">
      <!-- Scaled container: render at reference size, then scale down -->
      <div
        class="origin-top-left"
        :style="{
          width: `${referenceWidth}px`,
          height: `${referenceHeight}px`,
          transform: `scale(${scaleFactor})`,
        }">
        <!-- Render overlay for each editable element -->
        <div
          v-for="element in editableElements"
          :key="element.name"
          :style="getElementStyle(element)"
          :class="getElementClass(element)"
          @click="onElementClick($event, element.name)"
          @mouseenter="interactive && (hoveredFieldName = element.name)"
          @mouseleave="interactive && (hoveredFieldName = null)">
          <!-- Element name label (visible in editable zones mode or interactive hover/selected) -->
          <span
            v-if="showBorders || (interactive && (hoveredFieldName === element.name || selectedFieldName === element.name))"
            class="absolute top-0 left-0 px-1 py-0.5 text-3xs font-medium leading-none rounded-br z-10 whitespace-nowrap bg-status-warning text-status-warning-strong">
            {{ element.name }}
          </span>

          <!-- Text element overlay -->
          <SlideTextElement
            v-if="element.type === 'text'"
            :element="element"
            :bindings="bindings"
            :text-style-override="getTextStyle(element.name)" />

          <!-- Image element overlay (only when a custom image is set) -->
          <SlideImageElement
            v-else-if="element.type === 'image' && getImageUrl(element)"
            :image-url="getImageUrl(element)" />

          <!-- Chart element overlay -->
          <SlideChartElement
            v-else-if="element.type === 'chart'"
            :element="element"
            :bindings="bindings"
            :chart-templates="chartTemplates"
            :width="getElementDimensions(element).width"
            :height="getElementDimensions(element).height" />
        </div>
      </div>
    </div>
  </div>
</template>
